import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import BackflowTestDetail from './BackflowTestDetail';
import { BackflowTest, Job } from './types';
import Button from '../Button/Button';
import { getToken } from '../../services/auth';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import ThirdPaneFooter from '../ThirdPane/ThirdPaneFooter';
import JobHistory from '../Job/JobHistory';
import { Box, Flex, Text } from 'rebass';
import { ApprovalStatus } from '../../types/backflowTest';
import ApproveTestReportsModal from '../../modes/waterSupplier/pages/testReports/ApproveTestReportsModal';
import { PortalWithState } from 'react-portal';
import useNotify from '../../hooks/useNotify';
import useFormAssembly from '../../hooks/useFormAssembly';
import { APPROVE_BACKLOW_TEST, GET_TEST_REPORT } from './queries';
import StatusDot from '../Status/StatusDot';
import { ApolloError } from '@apollo/client/errors';

type BackflowTestThirdPaneProps = {
  jobId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
  reload?: () => void;
};

const downloadPdf = (jobId?: string) => {
  if (!jobId) {
    return;
  }
  const url = `${
    import.meta.env.ECOS_API_BASE
  }/test-reports/${jobId}?token=${getToken()}`;
  window.open(url);
};

type BackflowTestWithJob = BackflowTest & { job: Job };

export default function BackflowTestThirdPane({
  jobId,
  reload,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: BackflowTestThirdPaneProps): JSX.Element {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    backflowTests: BackflowTestWithJob[];
  }>(GET_TEST_REPORT, {
    skip: !jobId,
    variables: { where: { jobId } },
    fetchPolicy: 'network-only',
  });

  const backflowTest = itemData?.backflowTests?.[0];

  const advanceAndReload = useCallback(() => {
    if (onNext && nextEnabled) {
      onNext();
    }
    if (reload) {
      reload();
    }
  }, [onNext, nextEnabled]);

  const { showError } = useNotify();

  const [approveBackflowTest] = useMutation(APPROVE_BACKLOW_TEST, {
    variables: {
      jobId,
    },
    onCompleted: advanceAndReload,
    onError: useCallback((error: ApolloError) => {
      showError('Error approving backflow test report', error);
    }, []),
  });

  const downloadReport = useCallback(() => downloadPdf(jobId), [jobId]);

  const formAssembly = useFormAssembly(
    backflowTest?.job?.formData,
    backflowTest?.backflowAssembly
  );

  if (itemError) {
    throw itemError;
  }

  return (
    <ThirdPane visible={!!jobId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={backflowTest ? backflowTest?.jobId : 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      />
      <ThirdPaneContent>
        {backflowTest && (
          <Flex height="100%" flexDirection="row" flexGrow={1} flexShrink={1}>
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width={['50%', '50%', '66%']}
              overflowY="scroll"
            >
              <BackflowTestDetail
                backflowTest={backflowTest}
                job={backflowTest.job}
                formAssembly={formAssembly}
              />
            </Flex>

            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width={['50%', '50%', '33%']}
              sx={{ borderLeft: '1px solid', borderLeftColor: 'tableBorder' }}
            >
              <JobHistory
                jobId={backflowTest.jobId}
                comments={backflowTest.job.comments}
                statusChanges={backflowTest.job.statusChanges}
                attachments={backflowTest.job.attachments}
                refetch={refetch}
              />
            </Flex>
          </Flex>
        )}
      </ThirdPaneContent>
      {jobId && (
        <ThirdPaneFooter>
          {backflowTest && !backflowTest?.waterSupplierStatus && (
            <>
              <Flex flexDirection="row" sx={{ gap: 1 }} alignItems="center">
                <StatusDot status="blue" />
                <Text variant="subheading">Unsubmitted</Text>
              </Flex>
            </>
          )}
          {backflowTest?.waterSupplierStatus &&
            !(
              [
                'APPROVED_AUTOMATICALLY',
                'APPROVED_MANUALLY',
              ] as ApprovalStatus[]
            ).includes(backflowTest?.waterSupplierStatus as ApprovalStatus) && (
              <Button
                onClick={approveBackflowTest as () => void}
                icon="check-circle"
                variant="success"
                mr={3}
              >
                Approve
              </Button>
            )}

          {backflowTest?.waterSupplierStatus &&
            backflowTest?.waterSupplierStatus !== 'REJECTED' && (
              <PortalWithState closeOnOutsideClick closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button
                      variant="danger"
                      icon="slash-circle"
                      onClick={openPortal}
                    >
                      Reject
                    </Button>
                    {portal(
                      <ApproveTestReportsModal
                        action="REJECT"
                        jobIds={[jobId]}
                        onClose={(actionTaken) => {
                          if (actionTaken) {
                            advanceAndReload();
                          }
                          closePortal();
                        }}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            )}
          <Box flexGrow={1} textAlign="right">
            <Button
              variant="secondary"
              onClick={downloadReport}
              icon="file-download"
            >
              PDF
            </Button>
          </Box>
        </ThirdPaneFooter>
      )}
    </ThirdPane>
  );
}
